import { Select } from "antd";
import { useMemo, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomSelect, CustomTooltip } from "sipli-fleet-core/components";
import type { AuditLogCategory } from "src/services/auditLogService";

export interface AuditVehicleOption {
  vehicleId: string;
  unitId: string;
  label: string;
  vehicleName?: string;
  imei?: string;
  licensePlate?: string;
  serial?: string;
  vin?: string;
}

interface AuditLogFiltersProps {
  customerId?: string;
  vehicleId?: string;
  category: AuditLogCategory;
  customers: Array<{ value: string; label: string }>;
  vehicles: AuditVehicleOption[];
  isSearchLoading: boolean;
  isResetLoading: boolean;
  isResetDisabled: boolean;
  isVehiclesLoading: boolean;
  onCustomerChange: (customerId?: string) => void;
  onVehicleChange: (vehicleId?: string) => void;
  onCategoryChange: (category: AuditLogCategory) => void;
  onSubmit: () => void;
  onReset: () => void;
}

const AuditLogFilters = ({
  customerId,
  vehicleId,
  category,
  customers,
  vehicles,
  isSearchLoading,
  isResetLoading,
  isResetDisabled,
  isVehiclesLoading,
  onCustomerChange,
  onVehicleChange,
  onCategoryChange,
  onSubmit,
  onReset,
}: AuditLogFiltersProps) => {
  const { t } = useTranslation();

  const categoryOptions = useMemo(
    () => [
      { label: t("device"), value: "DEVICE" },
      { label: t("unit"), value: "UNIT" },
      { label: t("relay"), value: "RELAY" },
      { label: t("user"), value: "USER" },
      { label: t("geofence"), value: "GEOFENCE" },
    ],
    [t]
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form
      className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
      onSubmit={handleSubmit}
    >
      <CustomSelect
        label={t("customer")}
        className="common-select"
        containerClassName="w-full sm:w-[216px]"
        placeholder={t("select_customer")}
        value={customerId}
        options={customers}
        allowClear
        showSearch
        optionFilterProp="label"
        onChange={(value) => onCustomerChange(value ? String(value) : undefined)}
      />

      <CustomTooltip title={!customerId ? t("select_customer_first") : ""}>
        <div>
          <CustomSelect
            label={t("vehicle")}
            className="common-select"
            containerClassName="w-full sm:w-[216px]"
            placeholder={t("select_vehicle")}
            value={vehicleId}
            loading={isVehiclesLoading}
            disabled={!customerId}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.searchText as string)?.includes(input.toLowerCase())
            }
            optionLabelProp="label"
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            dropdownStyle={{ width: "max-content" }}
            onChange={(value) => onVehicleChange(value ? String(value) : undefined)}
          >
            {vehicles.map((vehicle) => (
              <Select.Option
                key={vehicle.vehicleId}
                value={vehicle.vehicleId}
                label={vehicle.licensePlate || t("unnamed_vehicle")}
                searchText={[
                  vehicle.vehicleName,
                  vehicle.licensePlate,
                  vehicle.imei,
                  vehicle.vin,
                  vehicle.serial,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase()}
              >
                <div className="flex flex-col py-1 text-sm" style={{ minWidth: 185 }}>
                  {vehicle.vehicleName ? (
                    <span>
                      <strong>{t("vehicle_name")}:</strong> {vehicle.vehicleName}
                    </span>
                  ) : null}

                  {vehicle.imei ? (
                    <span>
                      <strong>{t("imei")}:</strong> {vehicle.imei}
                    </span>
                  ) : null}

                  {vehicle.licensePlate ? (
                    <span>
                      <strong>{t("license_plate")}:</strong> {vehicle.licensePlate}
                    </span>
                  ) : null}

                  {vehicle.serial ? (
                    <span>
                      <strong>{t("serial_number")}:</strong> {vehicle.serial}
                    </span>
                  ) : null}

                  {vehicle.vin ? (
                    <span>
                      <strong>{t("vin")}:</strong> {vehicle.vin}
                    </span>
                  ) : null}
                </div>
              </Select.Option>
            ))}
          </CustomSelect>
        </div>
      </CustomTooltip>

      <CustomSelect
        label={t("category")}
        className="common-select"
        containerClassName="w-full sm:w-[216px]"
        value={category}
        options={categoryOptions}
        onChange={(value) => onCategoryChange(value as AuditLogCategory)}
      />

      <div className="flex gap-2 self-start sm:self-auto">
        <CustomButton
          htmlType="submit"
          type="primary"
          buttonSize="medium"
          className="!px-[14px] !text-gray2 !leading-[18px]"
          label={t("search")}
          loading={isSearchLoading}
          onClick={(event) => event.currentTarget.blur()}
        />
        <CustomButton
          type="secondary"
          buttonSize="medium"
          label={t("reset")}
          loading={isResetLoading}
          disabled={isResetDisabled}
          onClick={(event) => {
            event.currentTarget.blur();
            onReset();
          }}
        />
      </div>
    </form>
  );
};

export default AuditLogFilters;
